import { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";

export default function AdminSkus() {
  const [skus, setSkus] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", category: "UBBS" });

  const load = async () => {
    try {
      const { data } = await api.get("/admin/skus");
      setSkus(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    try {
      if (editing) await api.patch(`/admin/skus/${editing.id}`, form);
      else await api.post("/admin/skus", form);
      toast.success(editing ? "Updated" : "Created");
      setOpen(false);
      setEditing(null);
      setForm({ name: "", category: "UBBS" });
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const del = async (s) => {
    if (!window.confirm(`Delete SKU ${s.name}?`)) return;
    try {
      await api.delete(`/admin/skus/${s.id}`);
      toast.success("Deleted");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div>
      <div className="flex justify-end mb-4">
        <Button
          className="rounded-xl bg-slate-900 hover:bg-slate-700 text-white"
          onClick={() => {
            setEditing(null);
            setForm({ name: "", category: "UBBS" });
            setOpen(true);
          }}
          data-testid="add-sku-button"
        >
          <Plus className="h-4 w-4 mr-1" /> New SKU
        </Button>
      </div>

      <div className="glass-panel overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-white/50 border-b border-slate-200/70">
            <tr>
              <th className="text-left p-3 font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                Name
              </th>
              <th className="text-left p-3 font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                Category
              </th>
              <th className="text-right p-3 font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                Actions
              </th>
            </tr>
          </thead>
          <tbody data-testid="skus-table-rows">
            {skus.length === 0 ? (
              <tr>
                <td colSpan={3} className="p-6 text-center text-neutral-500">
                  No SKUs yet.
                </td>
              </tr>
            ) : (
              skus.map((s) => (
                <tr key={s.id} className="border-t border-slate-200/70">
                  <td className="p-3 font-medium">{s.name}</td>
                  <td className="p-3 font-mono text-xs">{s.category}</td>
                  <td className="p-3 text-right">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="rounded-xl h-8 w-8"
                      onClick={() => {
                        setEditing(s);
                        setForm({ name: s.name, category: s.category || "UBBS" });
                        setOpen(true);
                      }}
                      data-testid={`edit-sku-${s.id}`}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="rounded-xl h-8 w-8 text-red-600"
                      onClick={() => del(s)}
                      data-testid={`delete-sku-${s.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-xl">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">
              {editing ? "Edit SKU" : "Create SKU"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider">Name</Label>
              <Input
                className="mt-2 rounded-xl"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="sku-form-name"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Category</Label>
              <Input
                className="mt-2 rounded-xl"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                data-testid="sku-form-category"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              className="rounded-xl bg-slate-900 hover:bg-slate-700 text-white"
              onClick={save}
              data-testid="sku-form-save"
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
